import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Box, Center, Spinner } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useCancelOralExamBookingAllowRebooking } from '@/common/api/endpoints/oral-exams/oral-exams';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';
import { useExamLeaveGuardReporter } from '@/common/components/meeting/ExamLeaveGuard';
import { deriveExamLeaveGuard } from '@/common/components/meeting/deriveExamLeaveGuard';
import { OralExamErrorState } from '@/common/components/oralExam/OralExamErrorState';
import { MeetingRoom } from '@/common/components/meeting/MeetingRoom';
import { defaultMeetingJoinSettings } from '@/common/components/meeting/types';
import {
  clearCachedJoinCreds,
  clearLecturerSlotSession,
  readLecturerSlotSession,
  writeLecturerSlotSession,
} from '@/common/components/meeting/meetingSessionPersistence';
import { useMeetingLobbyState } from '@/common/components/meeting/useMeetingLobbyState';
import { useAuthStore } from '@/common/auth/store';
import { useOralExamCohortState } from '@/common/hooks/oralExam/useOralExamCohortState';
import { otherConnectedSupervisorNames } from '@/common/oralExam/supervisorVisits';
import { useOralExamIdReview } from '@/common/hooks/oralExam/useOralExamIdReview';
import { useLiveKitTokenRequest } from '@/common/hooks/oralExam/useLiveKitTokenRequest';
import { useOralExamMutations } from '@/common/hooks/oralExam/useOralExamMutations';
import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import {
  createLiveKitJoinHandle,
  createLiveKitSlotJoinHandle,
  getLiveKitJoinCacheKey,
} from '@/common/utils/oralExam/liveKitJoin';
import { getJoinErrorCode } from '@/common/utils/oralExam/joinErrorCode';
import {
  clearOralExamSentryTags,
  setOralExamSentryTags,
} from '@/common/utils/oralExamSentryTags';
import { isCohortExamSurface } from '@/common/utils/oralExam/runConduct';
import type { LiveKitJoinHandle } from '@/student/module/hooks/useEventTaskSlots';
import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';

import { CohortConsole } from './CohortConsole';
import { postDropInStepBackKeepalive } from './postDropInStepBackKeepalive';
import { LecturerCohortWrapUp } from './LecturerCohortWrapUp';
import { LecturerGroupBriefingStage } from './LecturerGroupBriefingStage';
import { LecturerOralExamSession } from './LecturerOralExamSession';
import { LecturerPreflightStage } from './LecturerPreflightStage';
import { LecturerPresenceSetupGate } from './LecturerPresenceSetupGate';
import { useLecturerPresenceSetupUpload } from './useLecturerPresenceSetupUpload';

/**
 * Keeps a lecturer in the pre-flight room while they watch the exam wall.
 * Students only see a face for a supervisor LiveKit still lists. Arriving
 * after Start never passes through that room, so this join is the presence.
 * Camera and mic stay off so a later Engage can take the devices.
 */
function SupervisorRosterPresence({
  slotId,
  token,
  serverUrl,
  onJoined,
  onRoomTokenConnected,
}: {
  slotId: string;
  token: string;
  serverUrl: string;
  onJoined: () => void;
  onRoomTokenConnected?: () => void;
}) {
  const announced = useRef(false);
  const onJoinedRef = useRef(onJoined);
  onJoinedRef.current = onJoined;
  const onRoomTokenConnectedRef = useRef(onRoomTokenConnected);
  onRoomTokenConnectedRef.current = onRoomTokenConnected;
  const handleConnected = useCallback(() => {
    onRoomTokenConnectedRef.current?.();
    if (announced.current) return;
    announced.current = true;
    onJoinedRef.current();
    setTimeout(() => onJoinedRef.current(), 800);
  }, []);

  return (
    <Box
      position="absolute"
      w={1}
      h={1}
      overflow="hidden"
      opacity={0}
      pointerEvents="none"
      testID="supervisor-roster-presence"
    >
      <MeetingRoom
        key={`roster-presence-${slotId}`}
        token={token}
        serverUrl={serverUrl}
        participantRole="lecturer"
        observabilitySurface="roster"
        joinSettings={{
          ...defaultMeetingJoinSettings,
          cameraEnabled: false,
          microphoneEnabled: false,
        }}
        onConnected={handleConnected}
      >
        <Box />
      </MeetingRoom>
    </Box>
  );
}

export type LecturerCohortSessionProps = {
  slotId: string;
  defaultIds: OralExamRouteIds | null;
  defaultLiveKitJoin: LiveKitJoinHandle;
  slotStartIso?: string | null;
  slotEndIso?: string | null;
  onClose: () => void;
  onGoToEvaluationDesk?: (moduleId: string) => void;
};

/**
 * Cohort mode orchestrator: pre-flight, cohort console (N≥2), or single-booking Room mode.
 */
function LecturerCohortSessionBody({
  slotId,
  defaultIds,
  defaultLiveKitJoin,
  slotStartIso,
  slotEndIso,
  onClose,
  onGoToEvaluationDesk,
}: LecturerCohortSessionProps) {
  const { t } = useTranslation();
  const savedSession = readLecturerSlotSession(slotId);
  const [presenceSetupDone, setPresenceSetupDone] = useState(
    () => savedSession?.presenceSetupDone ?? false,
  );
  const viewerLecturerId = useAuthStore((s) => s.user?.id ?? null);
  const {
    cohort, bookingRuns, capacity, queryKey, isLoading: cohortLoading, error: cohortError,
  } = useOralExamCohortState(
    slotId,
    { polling: true },
  );
  const { dropIn, isDroppingIn } = useOralExamIdReview({
    etag: cohort?.etag,
    stateQueryKey: queryKey,
    viewerLecturerId,
  });
  const { lobbyDone, joinSettings, onLobbyJoin } = useMeetingLobbyState({
    initialLobbyDone: savedSession?.lobbyDone ?? false,
    initialJoinSettings: savedSession?.joinSettings,
  });

  useEffect(() => {
    writeLecturerSlotSession(slotId, {
      presenceSetupDone,
      lobbyDone,
      joinSettings,
    });
  }, [joinSettings, lobbyDone, presenceSetupDone, slotId]);

  const phase = cohort?.sessionPhase;

  useEffect(() => {
    const viewerFocusRun = bookingRuns.find(
      (run) => run.supervisorStatus === OralExamSupervisorStatus.engaged
        || run.supervisorStatus === OralExamSupervisorStatus.here_with_you,
    );
    setOralExamSentryTags({
      slotId,
      phase: phase ?? undefined,
      bookingId: viewerFocusRun?.bookingId,
      questionIndex: viewerFocusRun?.runState?.currentQuestionIndex ?? undefined,
    });
    return () => {
      clearOralExamSentryTags();
    };
  }, [bookingRuns, phase, slotId]);
  const bookingRunCount = bookingRuns.length;
  // In-exam still mints a slot token so supervisors who were in the room at
  // Start stay on the roster. The token endpoint refuses anyone who was not.
  const prefetchBriefingToken = !cohort
    || phase === OralExamSessionPhase.pre_flight
    || phase === OralExamSessionPhase.group_briefing
    || (phase === OralExamSessionPhase.in_exam && capacity >= 2);
  const slotJoinHandle = useMemo(
    () => (prefetchBriefingToken ? createLiveKitSlotJoinHandle(slotId) : null),
    [prefetchBriefingToken, slotId],
  );
  const briefingTokenRequest = useLiveKitTokenRequest(slotJoinHandle);
  // Slot capacity routes preflight only when no booking runs exist yet.
  // Once runs are present, trust each run's decoded `mode` — never seat count.
  const isCohort = useMemo(() => {
    if (bookingRuns.length === 0) {
      return capacity >= 2;
    }
    const runWithMode = bookingRuns.find((run) => run.runState.mode);
    if (!runWithMode) {
      return false;
    }
    return isCohortExamSurface(runWithMode.runState);
  }, [bookingRuns, capacity]);

  const clearPersistedMeetingSession = useCallback(() => {
    clearLecturerSlotSession(slotId);
    const slotCacheKey = getLiveKitJoinCacheKey(createLiveKitSlotJoinHandle(slotId));
    if (slotCacheKey) clearCachedJoinCreds(slotCacheKey);
    const defaultCacheKey = getLiveKitJoinCacheKey(defaultLiveKitJoin);
    if (defaultCacheKey) clearCachedJoinCreds(defaultCacheKey);
  }, [defaultLiveKitJoin, slotId]);

  const handleDismissSession = useCallback(() => {
    clearPersistedMeetingSession();
    onClose();
  }, [clearPersistedMeetingSession, onClose]);

  const handleGoToEvaluationDesk = useCallback(() => {
    const moduleId = bookingRuns[0]?.moduleId ?? defaultIds?.moduleId;
    if (!moduleId || !onGoToEvaluationDesk) return;
    clearPersistedMeetingSession();
    onGoToEvaluationDesk(moduleId);
  }, [
    bookingRuns,
    clearPersistedMeetingSession,
    defaultIds?.moduleId,
    onGoToEvaluationDesk,
  ]);

  const mutationIds = useMemo((): OralExamRouteIds => {
    const first = bookingRuns[0];
    if (first) {
      return {
        courseId: first.courseId,
        moduleId: first.moduleId,
        taskId: first.taskId,
        bookingId: first.bookingId,
      };
    }
    return {
      courseId: defaultIds?.courseId ?? '',
      moduleId: defaultIds?.moduleId ?? '',
      taskId: defaultIds?.taskId ?? '',
      bookingId: defaultIds?.bookingId ?? '',
    };
  }, [bookingRuns, defaultIds]);

  const handleDropIn = useCallback(async (bookingId: string) => {
    await dropIn({
      slotId,
      bookingId,
      rung: 'speaking',
      etag: cohort?.etag,
    });
  }, [dropIn, slotId, cohort?.etag]);

  const handleObserve = useCallback(async (bookingId: string) => {
    await dropIn({
      slotId,
      bookingId,
      rung: 'observing',
      etag: cohort?.etag,
    });
  }, [cohort?.etag, dropIn, slotId]);

  const handleHide = useCallback(async () => {
    await dropIn({ slotId, bookingId: null, etag: cohort?.etag });
  }, [cohort?.etag, dropIn, slotId]);

  const handleLeave = useCallback(async () => {
    await dropIn({ slotId, bookingId: null, etag: cohort?.etag });
  }, [cohort?.etag, dropIn, slotId]);

  const leaveStepBack = useCallback(async () => {
    await dropIn({ slotId, bookingId: null, etag: cohort?.etag });
  }, [cohort?.etag, dropIn, slotId]);

  const leaveGuardReport = deriveExamLeaveGuard({
    capacity,
    sessionPhase: phase,
    bookingRuns,
    minSupervisors: cohort?.minSupervisors,
  });

  useExamLeaveGuardReporter({
    ...leaveGuardReport,
    maxUnsupervisedSeconds: cohort?.maxUnsupervisedSeconds ?? 0,
    otherSupervisorNames: viewerLecturerId
      ? otherConnectedSupervisorNames(cohort?.supervisors, viewerLecturerId)
      : [],
    absenceDeadlineAt: cohort?.supervisionAbsence?.deadlineAt ?? null,
    stepBack: cohort?.activeDropInBookingId ? leaveStepBack : null,
  });

  const cohortEtagRef = useRef(cohort?.etag);
  cohortEtagRef.current = cohort?.etag;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return undefined;
    }
    const onPageHide = () => {
      // The ordinary mutation is aborted once the document unloads.
      postDropInStepBackKeepalive(slotId, cohortEtagRef.current);
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [slotId]);

  const {
    abortExam, resetExam,
  } = useOralExamMutations(mutationIds, {
    etag: null,
    stateQueryKey: queryKey,
  });

  const cancelBookingMutation = useCancelOralExamBookingAllowRebooking();

  // "Remove from exam": CohortConsole resolves the latest booking summary at
  // confirm time and passes it here — no second roster lookup.
  const handleRemove = useCallback((summary: OralExamBookingRunSummary) => abortExam({
    courseId: summary.courseId,
    moduleId: summary.moduleId,
    taskId: summary.taskId,
    bookingId: summary.bookingId,
    etag: summary.runState.etag ?? null,
  }), [abortExam]);

  const handleReset = useCallback((summary: OralExamBookingRunSummary) => resetExam({
    courseId: summary.courseId,
    moduleId: summary.moduleId,
    taskId: summary.taskId,
    bookingId: summary.bookingId,
    etag: summary.runState.etag ?? null,
  }), [resetExam]);

  const handleCancel = useCallback((summary: OralExamBookingRunSummary) => {
    const headers = summary.runState.etag
      ? { 'If-Match': summary.runState.etag }
      : undefined;
    return cancelBookingMutation.mutateAsync({
      courseId: summary.courseId,
      moduleId: summary.moduleId,
      taskId: summary.taskId,
      bookingId: summary.bookingId,
      headers,
    });
  }, [cancelBookingMutation]);

  const engagedRun = bookingRuns.find(
    (run) => run.supervisorStatus === OralExamSupervisorStatus.engaged,
  );
  // Photo / lobby are in-memory. A reload during an underway exam session must not
  // send the lecturer back through presence setup or the pre-join lobby.
  const sessionAlreadyUnderway = phase === OralExamSessionPhase.in_exam
    || phase === OralExamSessionPhase.post_exam
    || Boolean(engagedRun);
  const skipPresenceGate = sessionAlreadyUnderway;
  const sessionLobbyDone = lobbyDone || sessionAlreadyUnderway;
  const completePresenceSetup = useCallback(() => setPresenceSetupDone(true), []);
  const {
    savedPhotoUrl,
    savedPhotoResolving,
    savedPhotoLoadFailed,
    retrySavedPhotoLoad,
    handlePresenceConfirm,
  } = useLecturerPresenceSetupUpload(completePresenceSetup);
  const cohortPresenceSetupPending = !presenceSetupDone
    && !skipPresenceGate
    && isCohort
    && lobbyDone;
  const cohortPreflightPresenceSetup = cohortPresenceSetupPending
    ? {
      onConfirm: handlePresenceConfirm,
      savedPhotoUrl,
      savedPhotoResolving,
      savedPhotoLoadFailed,
      retrySavedPhotoLoad,
    }
    : null;

  const missedExamStart = getJoinErrorCode(briefingTokenRequest.joinError) === 'session_started_without_you'
    || getJoinErrorCode(cohortError) === 'session_started_without_you';

  let content: React.ReactNode;

  if (missedExamStart) {
    content = (
      <OralExamErrorState
        testID="oral-exam-late-supervisor-refused"
        tone="warning"
        title={t('oralExam.lateSupervisor.title', {
          defaultValue: 'This exam already started',
        })}
        message={t('oralExam.lateSupervisor.body', {
          defaultValue: 'You were not in the room when it started, so you cannot join now.',
        })}
        leave={handleDismissSession}
        leaveLabel={t('oralExam.leaveRoom')}
      />
    );
  } else if (cohortLoading && !cohort) {
    content = (
      <Center flex={1} testID="lecturer-cohort-resolving">
        <Spinner size="large" />
      </Center>
    );
  } else if (!skipPresenceGate && !lobbyDone) {
    content = (
      <LecturerPresenceSetupGate
        deviceCheckDone={false}
        onDeviceCheckJoin={onLobbyJoin}
        savedPhotoUrl={savedPhotoUrl}
        savedPhotoResolving={savedPhotoResolving}
        savedPhotoLoadFailed={savedPhotoLoadFailed}
        retrySavedPhotoLoad={retrySavedPhotoLoad}
        handlePresenceConfirm={handlePresenceConfirm}
      />
    );
  } else if (cohortPresenceSetupPending) {
    content = (
      <LecturerPreflightStage
        slotId={slotId}
        slotStartIso={slotStartIso}
        slotEndIso={slotEndIso}
        presenceSetup={cohortPreflightPresenceSetup}
      />
    );
  } else if (!presenceSetupDone && !skipPresenceGate) {
    content = (
      <LecturerPresenceSetupGate
        deviceCheckDone
        onDeviceCheckJoin={onLobbyJoin}
        savedPhotoUrl={savedPhotoUrl}
        savedPhotoResolving={savedPhotoResolving}
        savedPhotoLoadFailed={savedPhotoLoadFailed}
        retrySavedPhotoLoad={retrySavedPhotoLoad}
        handlePresenceConfirm={handlePresenceConfirm}
      />
    );
  } else if (!cohort) {
    // No session record yet — treat as pre_flight so the lecturer sees the preflight
    // stage immediately (rather than an indefinite spinner). Zero-booking dry runs land here.
    content = (
      <LecturerPreflightStage
        slotId={slotId}
        slotStartIso={slotStartIso}
        slotEndIso={slotEndIso}
        joinSettings={joinSettings}
        joinCreds={briefingTokenRequest.joinCreds}
      />
    );
  } else if (phase === OralExamSessionPhase.post_exam) {
    content = (
      <LecturerCohortWrapUp
        slotId={slotId}
        bookingRuns={bookingRuns}
        onClose={handleDismissSession}
        onGoToEvaluationDesk={onGoToEvaluationDesk ? handleGoToEvaluationDesk : undefined}
      />
    );
  } else if (phase === OralExamSessionPhase.pre_flight) {
    content = (
      <LecturerPreflightStage
        slotId={slotId}
        slotStartIso={slotStartIso}
        slotEndIso={slotEndIso}
        joinSettings={joinSettings}
        joinCreds={briefingTokenRequest.joinCreds}
        onRoomTokenConnected={briefingTokenRequest.clearInSessionTokenRetryWait}
      />
    );
  } else if (phase === OralExamSessionPhase.group_briefing) {
    // A co-supervisor has no booking of their own, so defaultIds is null.
    // The briefing still belongs to them: they may be the person who pressed Continue.
    content = (
      <LecturerGroupBriefingStage
        slotId={slotId}
        capacity={bookingRunCount}
        etag={cohort?.etag}
        joinSettings={joinSettings}
        onClose={handleDismissSession}
        joinCreds={briefingTokenRequest.joinCreds}
        joinError={briefingTokenRequest.joinError}
        refetch={briefingTokenRequest.refetch}
        isLoading={briefingTokenRequest.isLoading}
        onRoomTokenConnected={briefingTokenRequest.clearInSessionTokenRetryWait}
      />
    );
  } else if (!isCohort && defaultIds) {
    content = (
      <LecturerOralExamSession
        ids={defaultIds}
        slotId={slotId}
        liveKitJoin={defaultLiveKitJoin}
        onClose={handleDismissSession}
        lobbyDone={sessionLobbyDone}
        joinSettings={joinSettings}
        onLobbyJoin={onLobbyJoin}
      />
    );
  } else {
    const targetIds: OralExamRouteIds | null = engagedRun
      ? {
        courseId: engagedRun.courseId,
        moduleId: engagedRun.moduleId,
        taskId: engagedRun.taskId,
        bookingId: engagedRun.bookingId,
      }
      : null;
    const rosterCreds = briefingTokenRequest.joinCreds;
    content = (
      <>
        {capacity >= 2 && rosterCreds?.token && rosterCreds.livekitUrl ? (
          <SupervisorRosterPresence
            slotId={slotId}
            token={rosterCreds.token}
            serverUrl={rosterCreds.livekitUrl}
            onJoined={briefingTokenRequest.refetch}
            onRoomTokenConnected={briefingTokenRequest.clearInSessionTokenRetryWait}
          />
        ) : null}
        <Box
          flex={engagedRun ? undefined : 1}
          minHeight={0}
          style={engagedRun ? { display: 'none' } : undefined}
          pointerEvents={engagedRun ? 'none' : 'auto'}
          testID="oral-exam-cohort-console-keep-alive"
        >
          <CohortConsole
            slotId={slotId}
            onDropIn={handleDropIn}
            onObserve={handleObserve}
            onHide={handleHide}
            onLeave={handleLeave}
            isRungMutationPending={isDroppingIn}
            onRemove={handleRemove}
            onReset={handleReset}
            onCancel={handleCancel}
          />
        </Box>
        {targetIds ? (
          <LecturerOralExamSession
            ids={targetIds}
            slotId={slotId}
            liveKitJoin={createLiveKitJoinHandle(targetIds)}
            onClose={handleLeave}
            lobbyDone={sessionLobbyDone}
            joinSettings={joinSettings}
            onLobbyJoin={onLobbyJoin}
            backLabel={t('oralExam.lecturer.backToOverview', {
              defaultValue: 'Return to overview',
            })}
            isDropIn
          />
        ) : null}
      </>
    );
  }

  return content;
}

export function LecturerCohortSession(props: LecturerCohortSessionProps) {
  return <LecturerCohortSessionBody {...props} />;
}
